import { GenderCategory } from "@/modules/profile/publicProfile/gateway/publicProfileDto"
import type { ColumnSortingOrder } from "@/modules/types/datatableTypes"
import type { Page } from "@betomorrow/micro-stores"
import { type BoxStaffItemDto, StaffColumnToSort } from "@modules/boxStaff/gateway/boxStaffDto"
import { MemberDetailedEntity } from "@modules/members/entities/memberEntity"
import { type MemberApiGateway, SESSION_HISTORY_STATUS_FILTER } from "@modules/members/gateway/memberApiGateway"
import {
  DAY_OF_WEEK,
  MemberColumnToSort,
  type MemberDetailedDto,
  MemberFilter,
  type MemberItemDto,
  type MemberSessionHistoryItemDto,
  type MemberSessionHistorySummaryDto,
  MemberStatus,
  SESSION_HISTORY_STATUS,
} from "@modules/members/gateway/memberDto"
import { MemberItemBuilder } from "@modules/members/tests/memberItem.builder"
import { MemberSessionHistoryItemBuilder } from "@modules/members/tests/memberSessionHistoryEntity.builder"
import { type Paginated, type PaginatedWrapper } from "@modules/utils/paginated"
import dayjs from "dayjs"

// ────────────────────────────────── Fake Member Details ──────────────────────────────────

const _MEMBER_DETAILS_DATA: MemberDetailedDto = {
  id: "member-123",
  userId: "user-123",
  firstname: "John",
  lastname: "Doe",
  inscriptionDate: "2024-01-15T10:00:00Z",
  status: MemberStatus.ACTIVE,
  username: "john.doe",
  birthDate: "1990-05-15",
  phoneWithPrefix: {
    number: "+1234567890",
    countryCode: "US",
  },
  email: "john.doe@example.com",
  address: {
    address: "123 Main St",
    additionalInformation: "Apt 42",
    city: "New York",
    postalCode: "10001",
    country: "USA",
  },
  emergencyInformation: "Contact Jane Doe at +1234567891",
  note: "Regular member, prefers morning classes",
  lastVisited: "2024-06-30T18:30:00Z",
  noShowPastMonth: 2,
  activeOffersTitles: ["Premium Membership", "Personal Training"],
  nextBookings: [
    {
      slotId: "slot-456",
      name: "CrossFit WOD",
      date: "2024-07-08T09:00:00Z",
      color: "#FF5733",
    },
    {
      slotId: "slot-789",
      name: "Yoga Class",
      date: "2024-07-10T18:00:00Z",
      color: "#33FF57",
    },
  ],
  publicProfile: {
    userId: "profile-123",
    username: "John D.",
    avatar: {
      url: "https://example.com/avatar.jpg",
      hash: "John's avatar",
    },
    firstname: "John",
    lastname: "Doe",
    biography: "John Doe is a regular member, prefers morning classes",
    birthdate: "1990-05-15",
    crossfitStartDate: "2020-01-01",
    genderCategory: GenderCategory.MALE,
    job: "Software Engineer",
    nationality: "USA",
    height: 180,
    weight: 70,
    favoritesPersonalRecords: [],
    ownedPrograms: [],
    followedPrograms: [],
    sharedCoachPrograms: [],
    badges: [],
    followedProgramBundle: [],
  },
}

// ────────────────────────────────── Fake Members (via Builder) ──────────────────────────────────

const _MEMBERS_DATA: MemberItemDto[] = [
  MemberItemBuilder.create()
    .withId("member-1")
    .withName("John", "Doe")
    .asActive()
    .withOffer("Premium Membership")
    .withLastVisitedDaysAgo(1)
    .withNoShowPastMonth(2)
    .withInscriptionDaysAgo(365)
    .buildDto(),
  MemberItemBuilder.create()
    .withId("member-2")
    .withName("Sarah", "Connor")
    .asActive()
    .withOffer("Unlimited Pass")
    .withLastVisitedDaysAgo(0)
    .withNoShowPastMonth(0)
    .withInscriptionDaysAgo(180)
    .buildDto(),
  MemberItemBuilder.create()
    .withId("member-3")
    .withName("Mike", "Johnson")
    .asActive()
    .withOffer("10-Session Pack")
    .withLastVisitedDaysAgo(3)
    .withNoShowPastMonth(1)
    .withInscriptionDaysAgo(90)
    .buildDto(),
  MemberItemBuilder.create()
    .withId("member-4")
    .withName("Emily", "Chen")
    .asProspect()
    .withLastVisitedDaysAgo(14)
    .withInscriptionDaysAgo(30)
    .buildDto(),
  MemberItemBuilder.create()
    .withId("member-5")
    .withName("Lucas", "Bernard")
    .asActive()
    .withOffer("Premium Membership")
    .withOffer("Personal Training")
    .withLastVisitedDaysAgo(0)
    .withNoShowPastMonth(0)
    .withInscriptionDaysAgo(730)
    .withNote("VIP member, morning classes only")
    .buildDto(),
  MemberItemBuilder.create()
    .withId("member-6")
    .withName("Sofia", "Martinez")
    .asTerminated()
    .withLastVisitedDaysAgo(60)
    .withInscriptionDaysAgo(400)
    .withLastMembership("Basic Membership")
    .buildDto(),
  MemberItemBuilder.create()
    .withId("member-7")
    .withName("Thomas", "Wilson")
    .asDropIn()
    .withLastVisitedDaysAgo(7)
    .withNoShowPastMonth(0)
    .withInscriptionDaysAgo(15)
    .buildDto(),
  MemberItemBuilder.create()
    .withId("member-8")
    .withName("Julie", "Dupont")
    .asActive()
    .withOffer("Unlimited Pass")
    .withLastVisitedDaysAgo(2)
    .withNoShowPastMonth(3)
    .withInscriptionDaysAgo(200)
    .withNote("Prefers evening classes")
    .buildDto(),
]

// ────────────────────────────────── Fake Session History (via Builder) ──────────────────────────────────

const _SESSION_HISTORY_DATA: MemberSessionHistoryItemDto[] = [
  // ── March 2026 (current month) ──
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 2, 4, 9, 0)
    .withProgram("CrossFit WOD", "#FF6B6B")
    .withCoach("Sandra", "Graham")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 2, 4, 18, 0)
    .withProgram("Yoga", "#9C27B0")
    .withCoach("Emily", "Chen")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 2, 3, 9, 0)
    .withProgram("CrossFit WOD", "#FF6B6B")
    .withCoach("Sandra", "Graham")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 2, 3, 7, 0)
    .withProgram("Open Gym", "#4CAF50")
    .withoutCoach()
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 2, 2, 9, 0)
    .withProgram("CrossFit WOD", "#FF6B6B")
    .withCoach("Marcus", "Johnson")
    .asNoShow()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 2, 2, 10, 30)
    .withProgram("Cycling", "#2196F3")
    .withCoach("Emily", "Chen")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 2, 1, 9, 0)
    .withProgram("CrossFit WOD", "#FF6B6B")
    .withCoach("Sandra", "Graham")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 2, 1, 18, 0)
    .withProgram("Yoga", "#9C27B0")
    .withCoach("Emily", "Chen")
    .asNoShow()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 2, 5, 10, 0)
    .withProgram("Powerlifting", "#FFC107")
    .withCoach("Marcus", "Johnson")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 2, 6, 12, 0)
    .withProgram("Mobility Flow", "#00BCD4")
    .withCoach("Sandra", "Graham")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 2, 7, 17, 0)
    .withProgram("Running Club", "#E91E63")
    .withoutCoach()
    .asAttended()
    .buildDto(),
  // ── February 2026 ──
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 1, 28, 9, 0)
    .withProgram("CrossFit WOD", "#FF6B6B")
    .withCoach("Sandra", "Graham")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 1, 26, 7, 0)
    .withProgram("Open Gym", "#4CAF50")
    .withoutCoach()
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 1, 24, 18, 0)
    .withProgram("Yoga", "#9C27B0")
    .withCoach("Emily", "Chen")
    .asNoShow()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 1, 20, 9, 0)
    .withProgram("CrossFit WOD", "#FF6B6B")
    .withCoach("Sandra", "Graham")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 1, 18, 10, 30)
    .withProgram("Cycling", "#2196F3")
    .withCoach("Marcus", "Johnson")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 1, 15, 9, 0)
    .withProgram("CrossFit WOD", "#FF6B6B")
    .withCoach("Sandra", "Graham")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 1, 12, 7, 0)
    .withProgram("Open Gym", "#4CAF50")
    .withoutCoach()
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 1, 10, 18, 0)
    .withProgram("Yoga", "#9C27B0")
    .withCoach("Emily", "Chen")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 1, 5, 9, 0)
    .withProgram("CrossFit WOD", "#FF6B6B")
    .withCoach("Marcus", "Johnson")
    .asAttended()
    .buildDto(),
  // ── January 2026 ──
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 0, 28, 9, 0)
    .withProgram("CrossFit WOD", "#FF6B6B")
    .withCoach("Sandra", "Graham")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 0, 15, 7, 0)
    .withProgram("Open Gym", "#4CAF50")
    .withCoach("Marcus", "Johnson")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 0, 10, 9, 0)
    .withProgram("CrossFit WOD", "#FF6B6B")
    .withCoach("Sandra", "Graham")
    .asNoShow()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2026, 0, 5, 18, 0)
    .withProgram("Yoga", "#9C27B0")
    .withCoach("Emily", "Chen")
    .asAttended()
    .buildDto(),
  // ── December 2025 ──
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2025, 11, 20, 18, 0)
    .withProgram("Yoga", "#9C27B0")
    .withCoach("Emily", "Chen")
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2025, 11, 15, 9, 0)
    .withProgram("CrossFit WOD", "#FF6B6B")
    .withCoach("Sandra", "Graham")
    .asAttended()
    .buildDto(),
  // ── November 2025 ──
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2025, 10, 28, 7, 0)
    .withProgram("Open Gym", "#4CAF50")
    .withoutCoach()
    .asAttended()
    .buildDto(),
  MemberSessionHistoryItemBuilder.create()
    .withDateAt(2025, 10, 20, 9, 0)
    .withProgram("CrossFit WOD", "#FF6B6B")
    .withCoach("Marcus", "Johnson")
    .asNoShow()
    .buildDto(),
]

// ────────────────────────────────── Helpers ──────────────────────────────────

function scopeByPeriod(sessions: MemberSessionHistoryItemDto[], year: number, month: number | null) {
  return sessions.filter((s) => {
    const d = dayjs(s.startDate)
    if (month !== null) {
      return d.year() === year && d.month() + 1 === month
    }
    return d.year() === year
  })
}

// ────────────────────────────────── FakeMemberApi ──────────────────────────────────

export class FakeMemberApi implements MemberApiGateway {
  private members: MemberItemDto[] = [..._MEMBERS_DATA]
  private sessionHistory: MemberSessionHistoryItemDto[] = [..._SESSION_HISTORY_DATA]

  givenMembers(members: MemberItemDto[]) {
    this.members = members
  }

  givenSessionHistory(sessions: MemberSessionHistoryItemDto[]) {
    this.sessionHistory = sessions
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async getMembers(
    _filter: MemberFilter,
    page = 0,
    size = 10,
    _nameFilter?: string,
    _sortBy?: MemberColumnToSort,
    _sortOrder?: ColumnSortingOrder,
  ): Promise<Page<MemberItemDto>> {
    const start = page * size
    const end = start + size
    const content = this.members.slice(start, end)

    return {
      content,
      page,
      totalPages: Math.ceil(this.members.length / size),
      totalSize: this.members.length,
    }
  }

  getMember(_memberId: string): Promise<MemberDetailedDto> {
    return Promise.resolve({ ..._MEMBER_DETAILS_DATA })
  }

  updateMember(_memberId: string, _member: MemberDetailedEntity): Promise<MemberDetailedDto> {
    return Promise.resolve({ ..._MEMBER_DETAILS_DATA })
  }

  archiveMember(_memberId: string): Promise<void> {
    return Promise.resolve()
  }

  getArchivedMembers(
    page?: number,
    size?: number,
    _nameFilter?: string,
    _sortBy?: MemberColumnToSort,
    _sortOrder?: ColumnSortingOrder,
  ): Promise<PaginatedWrapper<MemberItemDto>> {
    return Promise.resolve({
      content: [],
      pageable: {
        offset: 0,
        sort: { direction: "ASC", nullHandling: "NATIVE", ascending: true, property: "id", ignoreCase: false },
        paged: true,
        pageNumber: page ?? 0,
        pageSize: size ?? 10,
        unpaged: false,
      },
      totalElements: 0,
      totalPages: 0,
      last: true,
      size: size ?? 10,
      number: page ?? 0,
      first: true,
      empty: true,
      sort: { direction: "ASC", nullHandling: "NATIVE", ascending: true, property: "id", ignoreCase: false },
    })
  }

  getArchivedStaffMembers(
    page?: number,
    size?: number,
    _nameFilter?: string,
    _sortBy?: StaffColumnToSort,
    _sortOrder?: ColumnSortingOrder,
  ): Promise<PaginatedWrapper<BoxStaffItemDto>> {
    return Promise.resolve({
      content: [],
      pageable: {
        offset: 0,
        sort: { direction: "ASC", nullHandling: "NATIVE", ascending: true, property: "id", ignoreCase: false },
        paged: true,
        pageNumber: page ?? 0,
        pageSize: size ?? 10,
        unpaged: false,
      },
      totalElements: 0,
      totalPages: 0,
      last: true,
      size: size ?? 10,
      number: page ?? 0,
      first: true,
      empty: true,
      sort: { direction: "ASC", nullHandling: "NATIVE", ascending: true, property: "id", ignoreCase: false },
    })
  }

  // ── Summary endpoint ──
  getMemberSessionHistorySummary(
    _memberId: string,
    year: number,
    month: number | null,
  ): Promise<MemberSessionHistorySummaryDto> {
    const scoped = scopeByPeriod(this.sessionHistory, year, month)

    const presentCount = scoped.filter((s) => s.status === SESSION_HISTORY_STATUS.PRESENT).length
    const noShowCount = scoped.filter((s) => s.status === SESSION_HISTORY_STATUS.NO_SHOW).length

    // Program breakdown
    const programMap = new Map<string, { color: string; count: number }>()
    for (const s of scoped) {
      const existing = programMap.get(s.name)
      if (existing) {
        existing.count++
      } else {
        programMap.set(s.name, { color: s.color, count: 1 })
      }
    }
    const resume = Array.from(programMap.entries()).map(([name, { color, count }]) => ({
      name,
      color,
      count,
      archived: false,
    }))

    // Favorite slots (top 3)
    const slotMap = new Map<
      string,
      { name: string; color: string; dayOfWeek: DAY_OF_WEEK; startTime: string; endTime: string; count: number }
    >()
    for (const s of scoped) {
      const d = dayjs(s.startDate)
      const days = [
        DAY_OF_WEEK.SUNDAY,
        DAY_OF_WEEK.MONDAY,
        DAY_OF_WEEK.TUESDAY,
        DAY_OF_WEEK.WEDNESDAY,
        DAY_OF_WEEK.THURSDAY,
        DAY_OF_WEEK.FRIDAY,
        DAY_OF_WEEK.SATURDAY,
      ]
      const dayOfWeek = days[d.day()] as DAY_OF_WEEK
      const startTime = d.format("HH:mm:ss")
      const endTime = dayjs(s.endDate).format("HH:mm:ss")
      const key = `${s.name}-${dayOfWeek}-${startTime}-${endTime}`
      const existing = slotMap.get(key)
      if (existing) {
        existing.count++
      } else {
        slotMap.set(key, { name: s.name, color: s.color, dayOfWeek, startTime, endTime, count: 1 })
      }
    }
    const favorites = Array.from(slotMap.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)

    return Promise.resolve({
      pageCounter: {
        all: scoped.length,
        present: presentCount,
        noShow: noShowCount,
      },
      resume,
      favorites,
    })
  }

  // ── List endpoint (paginated + filtered) ──
  async getMemberSessionHistoryList(
    _memberId: string,
    year: number,
    month: number | null,
    filter: SESSION_HISTORY_STATUS_FILTER,
    page = 0,
    pageSize = 20,
  ): Promise<Paginated<MemberSessionHistoryItemDto>> {
    let scoped = scopeByPeriod(this.sessionHistory, year, month)

    if (filter === SESSION_HISTORY_STATUS_FILTER.PRESENT) {
      scoped = scoped.filter((s) => s.status === SESSION_HISTORY_STATUS.PRESENT)
    } else if (filter === SESSION_HISTORY_STATUS_FILTER.NO_SHOW) {
      scoped = scoped.filter((s) => s.status === SESSION_HISTORY_STATUS.NO_SHOW)
    }

    scoped = [...scoped].sort((a, b) => dayjs(b.startDate).valueOf() - dayjs(a.startDate).valueOf())

    const start = page * pageSize
    const end = start + pageSize
    const content = scoped.slice(start, end)

    return Promise.resolve({
      content,
      contentSize: content.length,
      offset: start,
      page,
      pageSize,
      totalElements: scoped.length,
      totalPages: Math.ceil(scoped.length / pageSize),
      last: end >= scoped.length,
    })
  }
}
